import React, { useState } from 'react';
import { AnomalyDetectionWidget } from './AnomalyDetectionWidget';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { 
  Users, 
  Clock, 
  TrendingUp, 
  Award,
  AlertTriangle,
  Download,
  Target,
  Zap,
  CheckCircle2,
  XCircle,
  BarChart3,
  Activity,
  Trophy,
  Star
} from 'lucide-react';
import { Progress } from './ui/progress';
import { Avatar, AvatarFallback } from './ui/avatar';

interface EmployeeMetrics {
  id: string;
  name: string;
  email: string;
  workflowsCreated: number;
  workflowsApproved: number;
  workflowsRejected: number;
  workflowsInProduction: number;
  totalTimeSpent: number; // en horas
  avgTimePerWorkflow: number; // en horas
  approvalRate: number; // porcentaje
  lastActivity: Date;
  productivity: 'high' | 'medium' | 'low';
  certificates: string[];
}

const mockEmployeeMetrics: EmployeeMetrics[] = [
  {
    id: '1',
    name: 'Juan Pérez',
    email: 'juan.perez@zaphub.com',
    workflowsCreated: 8,
    workflowsApproved: 7,
    workflowsRejected: 1,
    workflowsInProduction: 5,
    totalTimeSpent: 42.5,
    avgTimePerWorkflow: 5.3,
    approvalRate: 87.5,
    lastActivity: new Date('2024-02-05'),
    productivity: 'high',
    certificates: ['Automation Expert', 'AI Integration'],
  },
  {
    id: '2',
    name: 'María González',
    email: 'maria.gonzalez@zaphub.com',
    workflowsCreated: 5,
    workflowsApproved: 4,
    workflowsRejected: 1,
    workflowsInProduction: 3,
    totalTimeSpent: 28.0,
    avgTimePerWorkflow: 5.6,
    approvalRate: 80.0,
    lastActivity: new Date('2024-02-04'),
    productivity: 'high',
    certificates: ['Data Transform'],
  },
  {
    id: '3',
    name: 'Pedro Ramírez',
    email: 'pedro.ramirez@zaphub.com',
    workflowsCreated: 3,
    workflowsApproved: 2,
    workflowsRejected: 1,
    workflowsInProduction: 1,
    totalTimeSpent: 35.0,
    avgTimePerWorkflow: 11.7,
    approvalRate: 66.7,
    lastActivity: new Date('2024-02-04'),
    productivity: 'medium',
    certificates: [],
  },
  {
    id: '4',
    name: 'Ana López',
    email: 'ana.lopez@zaphub.com',
    workflowsCreated: 6,
    workflowsApproved: 5,
    workflowsRejected: 1,
    workflowsInProduction: 4,
    totalTimeSpent: 31.5,
    avgTimePerWorkflow: 5.25,
    approvalRate: 83.3,
    lastActivity: new Date('2024-02-03'),
    productivity: 'high',
    certificates: ['Integration Master'],
  },
  {
    id: '5',
    name: 'Carlos Mendoza',
    email: 'carlos.mendoza@zaphub.com',
    workflowsCreated: 10,
    workflowsApproved: 9,
    workflowsRejected: 1,
    workflowsInProduction: 7,
    totalTimeSpent: 48.0,
    avgTimePerWorkflow: 4.8,
    approvalRate: 90.0,
    lastActivity: new Date('2024-02-05'),
    productivity: 'high',
    certificates: ['Automation Expert', 'Logic Master', 'Speed Developer'],
  },
  {
    id: '6',
    name: 'Luis Torres',
    email: 'luis.torres@zaphub.com',
    workflowsCreated: 2,
    workflowsApproved: 0,
    workflowsRejected: 2,
    workflowsInProduction: 0,
    totalTimeSpent: 24.0,
    avgTimePerWorkflow: 12.0,
    approvalRate: 0,
    lastActivity: new Date('2024-02-01'),
    productivity: 'low',
    certificates: [],
  },
];

export function AdminAnalyticsDashboard() {
  const [employees] = useState<EmployeeMetrics[]>(mockEmployeeMetrics);
  const [sortBy, setSortBy] = useState<'productivity' | 'approvalRate' | 'totalTime'>('productivity');

  const totalWorkflows = employees.reduce((sum, e) => sum + e.workflowsCreated, 0);
  const totalApproved = employees.reduce((sum, e) => sum + e.workflowsApproved, 0);
  const totalRejected = employees.reduce((sum, e) => sum + e.workflowsRejected, 0);
  const totalTimeSpent = employees.reduce((sum, e) => sum + e.totalTimeSpent, 0);
  const avgApprovalRate = employees.reduce((sum, e) => sum + e.approvalRate, 0) / employees.length;

  const topPerformers = [...employees]
    .sort((a, b) => b.approvalRate - a.approvalRate)
    .slice(0, 3);

  const needsAttention = employees.filter(e => 
    e.productivity === 'low' || e.approvalRate < 50
  );

  const getProductivityBadge = (productivity: EmployeeMetrics['productivity']) => {
    const configs = {
      high: { label: 'Alta', variant: 'default' as const, color: 'bg-green-500' },
      medium: { label: 'Media', variant: 'secondary' as const, color: 'bg-yellow-500' },
      low: { label: 'Baja', variant: 'destructive' as const, color: 'bg-red-500' },
    };
    return configs[productivity];
  };

  const formatHours = (hours: number): string => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    return `${hours.toFixed(1)}h`;
  };

  const exportReport = () => {
    const csvContent = [
      ['Empleado', 'Email', 'Workflows Creados', 'Aprobados', 'Rechazados', 'Tasa Aprobación', 'Tiempo Total', 'Tiempo Promedio', 'Productividad'].join(','),
      ...employees.map(e => [
        e.name,
        e.email,
        e.workflowsCreated,
        e.workflowsApproved,
        e.workflowsRejected,
        `${e.approvalRate}%`,
        `${e.totalTimeSpent}h`,
        `${e.avgTimePerWorkflow}h`,
        e.productivity,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-empleados-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Anomaly Detection */}
      <AnomalyDetectionWidget autoRefreshSecs={120} showMax={5} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Analytics de Empleados</h2>
          <p className="text-sm text-muted-foreground">
            Métricas de productividad y rendimiento del equipo
          </p>
        </div>
        <Button onClick={exportReport} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Exportar Reporte
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Empleados Activos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{employees.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {employees.filter(e => e.productivity === 'high').length} alta productividad
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Tasa de Aprobación
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{avgApprovalRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalApproved}/{totalWorkflows} workflows
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-purple-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Tiempo Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalTimeSpent.toFixed(0)}h</div>
            <p className="text-xs text-muted-foreground mt-1">
              En desarrollo
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-orange-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Target className="h-4 w-4" />
              Promedio/Workflow
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {(totalTimeSpent / totalWorkflows).toFixed(1)}h
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Por automatización
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-red-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Requieren Atención
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{needsAttention.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Baja productividad
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Top Performers */}
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500"></div>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Top Performers
            </CardTitle>
            <CardDescription>Empleados con mejor rendimiento</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {topPerformers.map((emp, index) => (
              <div
                key={emp.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-accent/50"
              >
                <div className="relative">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary text-white">
                      {emp.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  {index === 0 && (
                    <div className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-500 text-white">
                      <Star className="h-3 w-3 fill-current" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{emp.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">
                      {emp.approvalRate.toFixed(0)}% aprobación
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {emp.workflowsInProduction} en prod.
                    </span>
                  </div>
                </div>
                <div className="text-2xl font-bold text-muted-foreground">
                  #{index + 1}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Needs Attention */}
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-red-500"></div>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Requieren Seguimiento
            </CardTitle>
            <CardDescription>Empleados con baja productividad</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {needsAttention.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
                ¡Todos los empleados están rindiendo bien!
              </div>
            ) : (
              needsAttention.map((emp) => (
                <div
                  key={emp.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-red-500 text-white">
                      {emp.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{emp.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="destructive" className="text-xs">
                        {emp.approvalRate.toFixed(0)}% aprobación
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {emp.workflowsRejected} rechazados
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Estadísticas Rápidas
            </CardTitle>
            <CardDescription>Resumen del equipo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Workflows en Producción</span>
                <span className="text-sm font-medium">
                  {employees.reduce((sum, e) => sum + e.workflowsInProduction, 0)}
                </span>
              </div>
              <Progress 
                value={(employees.reduce((sum, e) => sum + e.workflowsInProduction, 0) / totalWorkflows) * 100} 
                className="h-2"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Certificados Otorgados</span>
                <span className="text-sm font-medium">
                  {employees.reduce((sum, e) => sum + e.certificates.length, 0)}
                </span>
              </div>
              <Progress 
                value={(employees.filter(e => e.certificates.length > 0).length / employees.length) * 100} 
                className="h-2"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Alta Productividad</span>
                <span className="text-sm font-medium">
                  {employees.filter(e => e.productivity === 'high').length}/{employees.length}
                </span>
              </div>
              <Progress 
                value={(employees.filter(e => e.productivity === 'high').length / employees.length) * 100} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Employee Table */}
      <Card className="relative overflow-hidden border-border">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
        <CardHeader>
          <CardTitle className="text-base">Detalle por Empleado</CardTitle>
          <CardDescription>Métricas completas de productividad y tiempo</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-96">
            <div className="space-y-2">
              {employees
                .sort((a, b) => {
                  if (sortBy === 'approvalRate') return b.approvalRate - a.approvalRate;
                  if (sortBy === 'totalTime') return b.totalTimeSpent - a.totalTimeSpent;
                  return b.workflowsInProduction - a.workflowsInProduction;
                })
                .map((emp) => {
                  const prodConfig = getProductivityBadge(emp.productivity);
                  return (
                    <Card key={emp.id} className="border-border">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-primary text-white">
                              {emp.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 space-y-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-medium">{emp.name}</h3>
                                <p className="text-sm text-muted-foreground">{emp.email}</p>
                              </div>
                              <Badge variant={prodConfig.variant}>
                                {prodConfig.label}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-4 gap-4">
                              <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Workflows Creados</p>
                                <p className="text-lg font-semibold">{emp.workflowsCreated}</p>
                              </div>
                              <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Tasa Aprobación</p>
                                <p className="text-lg font-semibold">{emp.approvalRate.toFixed(0)}%</p>
                              </div>
                              <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Tiempo Total</p>
                                <p className="text-lg font-semibold">{formatHours(emp.totalTimeSpent)}</p>
                              </div>
                              <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Promedio/Workflow</p>
                                <p className="text-lg font-semibold">{formatHours(emp.avgTimePerWorkflow)}</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2 text-sm">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                                <span>{emp.workflowsApproved} aprobados</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <XCircle className="h-4 w-4 text-red-500" />
                                <span>{emp.workflowsRejected} rechazados</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Zap className="h-4 w-4 text-primary" />
                                <span>{emp.workflowsInProduction} en producción</span>
                              </div>
                            </div>

                            {emp.certificates.length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                {emp.certificates.map((cert) => (
                                  <Badge key={cert} variant="secondary" className="gap-1">
                                    <Award className="h-3 w-3" />
                                    {cert}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
